'use client';
import React from 'react';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Navigation, RefreshCw, Eye, EyeOff, Phone, ShieldCheck, AlertCircle, Copy, Upload, X, FileText as FileTextIcon, Gift, Calendar, Star, Check, Users, GitBranch, UserCheck, Dumbbell, Zap } from 'lucide-react';
import type { UseFormRegister, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import type { DbPlan } from './SignupForm';

type SignupStep = 'phone-verify' | 'account' | 'gym' | 'branch' | 'documents' | 'plan' | 'confirm';

interface SignupValues {
  fullName: string;
  phone: string;
  gymName: string;
  gymType: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gymPhone: string;
  gymEmail: string;
  branchName: string;
  branchAddress: string;
  branchCity: string;
  branchPhone: string;
  branchLat: string;
  branchLng: string;
  geofenceRadius: string;
}

interface UploadedDoc {
  name: string;
  type: string;
  url: string;
  size: number;
  _file?: File;
}

interface DocType {
  value: string;
  label: string;
}

interface SignupFormViewProps {
  step: SignupStep;
  steps: { id: SignupStep; label: string; icon: React.ElementType }[];
  currentStepIdx: number;
  visitedSteps: Set<SignupStep>;
  navigateToStep: (s: SignupStep) => void;
  onBack: () => void;
  handleSubmit: UseFormHandleSubmit<SignupValues>;
  onSubmit: (data: SignupValues) => Promise<void>;
  verifyPhone: string;
  setVerifyPhone: (v: string) => void;
  otp: string;
  setOtp: (v: string) => void;
  otpSent: boolean;
  otpVerified: boolean;
  otpError: string;
  setOtpError: (v: string) => void;
  devOtp: string;
  resendCooldown: number;
  otpLoading: boolean;
  otpInputRef: React.RefObject<HTMLInputElement | null>;
  handleSendOtp: () => Promise<void>;
  handleVerifyOtp: () => Promise<void>;
  handleResendOtp: () => Promise<void>;
  register: UseFormRegister<SignupValues>;
  errors: FieldErrors<SignupValues>;
  watchedValues: Partial<SignupValues>;
  pin: string;
  setPin: (v: string) => void;
  confirmPin: string;
  setConfirmPin: (v: string) => void;
  showPin: boolean;
  setShowPin: (v: boolean | ((prev: boolean) => boolean)) => void;
  showConfirmPin: boolean;
  setShowConfirmPin: (v: boolean | ((prev: boolean) => boolean)) => void;
  pinRef: React.RefObject<HTMLInputElement | null>;
  confirmPinRef: React.RefObject<HTMLInputElement | null>;
  otpVerifiedPhone: string;
  sameGymPhone: boolean;
  handleSameGymPhone: (v: boolean) => void;
  sameBranchAddress: boolean;
  handleSameBranchAddress: (v: boolean) => void;
  sameBranchCity: boolean;
  handleSameBranchCity: (v: boolean) => void;
  sameBranchPhone: boolean;
  handleSameBranchPhone: (v: boolean) => void;
  handleGetLocation: () => void;
  gettingLocation: boolean;
  uploadedDocs: UploadedDoc[];
  uploading: boolean;
  handleDocUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  removeDoc: (idx: number) => void;
  updateDocType: (idx: number, type: string) => void;
  selectedPlan: string;
  setSelectedPlan: (v: string) => void;
  billingCycle: 'monthly' | 'yearly';
  setBillingCycle: (v: 'monthly' | 'yearly') => void;
  selectedPlanData: DbPlan | undefined;
  activePlanPrice: number;
  dbPlans: DbPlan[];
  plansLoading: boolean;
  docTypes: DocType[];
  consent: boolean;
  setConsent: (v: boolean) => void;
  isLoading: boolean;
  nextStep: () => Promise<void>;
  prevStep: () => void;
}

export default function SignupFormView({
  step, steps, currentStepIdx, visitedSteps, navigateToStep, onBack,
  handleSubmit, onSubmit,
  verifyPhone, setVerifyPhone, otp, setOtp, otpSent, otpVerified, otpError, setOtpError,
  devOtp, resendCooldown, otpLoading, otpInputRef, handleSendOtp, handleVerifyOtp, handleResendOtp,
  register, errors, watchedValues, pin, setPin, confirmPin, setConfirmPin,
  showPin, setShowPin, showConfirmPin, setShowConfirmPin, pinRef, confirmPinRef, otpVerifiedPhone,
  sameGymPhone, handleSameGymPhone,
  sameBranchAddress, handleSameBranchAddress, sameBranchCity, handleSameBranchCity,
  sameBranchPhone, handleSameBranchPhone, handleGetLocation, gettingLocation,
  uploadedDocs, uploading, handleDocUpload, removeDoc, updateDocType,
  selectedPlan, setSelectedPlan, billingCycle, setBillingCycle,
  selectedPlanData, activePlanPrice, dbPlans, plansLoading, docTypes,
  consent, setConsent, isLoading, nextStep, prevStep,
}: SignupFormViewProps) {

  // Compute yearly savings for a plan
  const getYearlySavings = (plan: DbPlan) => {
    if (!plan.yearly_price || !plan.price) return 0;
    return (plan.price * 12) - plan.yearly_price;
  };

  // Compute discount label from DB data
  const getDiscountLabel = (plan: DbPlan) => {
    const pct = plan.yearly_discount_percent ?? 0;
    if (pct > 0) return `Save ${Math.round(pct)}%`;
    const savings = getYearlySavings(plan);
    if (savings > 0) return `Save ₹${savings.toLocaleString('en-IN')}`;
    return null;
  };

  // Get the best discount label across all plans for the toggle badge
  const getBestYearlyDiscount = () => {
    if (!dbPlans.length) return null;
    let bestPct = 0;
    for (const p of dbPlans) {
      const pct = p.yearly_discount_percent ?? 0;
      if (pct > bestPct) bestPct = pct;
    }
    if (bestPct > 0) return `Save up to ${Math.round(bestPct)}%`;
    return null;
  };

  // Format limit display
  const formatLimit = (val: number | null | undefined, label: string) => {
    if (val === null || val === undefined || val === 0) return { value: 'Unlimited', isUnlimited: true };
    if (val >= 9999) return { value: 'Unlimited', isUnlimited: true };
    return { value: `Up to ${val.toLocaleString('en-IN')}`, isUnlimited: false };
  };

  // Get price to display
  const getPlanPrice = (plan: DbPlan) => {
    if (billingCycle === 'yearly') {
      return plan.yearly_price ?? plan.price;
    }
    return plan.price;
  };

  // Get future payment amount for confirm step
  const getFuturePrice = () => {
    if (!selectedPlanData) return null;
    const price = getPlanPrice(selectedPlanData);
    const suffix = billingCycle === 'yearly' ? '/year' : '/month';
    return `₹${price.toLocaleString('en-IN')}${suffix}`;
  };

  const trialDays = selectedPlanData?.trial_days ?? 30;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors">
        <ArrowLeft size={14} /> Back to Sign In
      </button>

      <h2 className="font-display text-display-lg text-foreground mb-1">Register Your Gym</h2>
      <p className="text-sm text-muted-foreground mb-5">Get started with GYM VRIDHI in minutes.</p>

      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-7 overflow-x-auto pb-1">
        {steps.map((s, i) => {
          const StepIcon = s.icon;
          const isActive = s.id === step;
          const isDone = currentStepIdx > i;
          const isVisited = visitedSteps.has(s.id);
          const isClickable = isVisited && !isActive;
          return (
            <React.Fragment key={`step-${s.id}`}>
              <div
                className={`flex flex-col items-center gap-0.5 shrink-0 transition-all ${isActive ? 'text-primary' : isDone ? 'text-success' : 'text-muted-foreground'} ${isClickable ? 'cursor-pointer hover:opacity-80' : ''}`}
                onClick={() => isClickable && navigateToStep(s.id)}
                title={isClickable ? `Go to ${s.label}` : undefined}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={e => { if (isClickable && (e.key === 'Enter' || e.key === ' ')) navigateToStep(s.id); }}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${isActive ? 'border-primary bg-primary/10' : isDone ? 'border-success bg-success/10' : 'border-border bg-muted'}`}>
                  {isDone ? '✓' : <StepIcon size={12} />}
                </div>
                <span className="text-2xs font-medium hidden sm:block">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px min-w-[8px] transition-all ${isDone ? 'bg-success' : 'bg-border'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>

        {/* ── STEP 0: PHONE VERIFICATION ── */}
        {step === 'phone-verify' && (
          <div className="space-y-4 fade-in">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
                <ShieldCheck size={18} className="text-secondary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Verify Your Phone</h3>
                <p className="text-xs text-muted-foreground">We&apos;ll send a 6-digit OTP to confirm your number</p>
              </div>
            </div>

            <div>
              <label htmlFor="verify-phone" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Phone Number *</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id="verify-phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="9876543210"
                    autoComplete="tel"
                    value={verifyPhone}
                    onChange={e => { setVerifyPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setOtpError(''); }}
                    disabled={otpVerified}
                    className="w-full bg-input border border-border rounded-xl pl-9 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all disabled:opacity-60"
                  />
                </div>
                {!otpVerified && (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpLoading || verifyPhone.length < 10 || resendCooldown > 0}
                    className="px-4 py-3 rounded-xl bg-primary/10 border border-primary/30 text-primary text-sm font-semibold hover:bg-primary/20 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {otpLoading ? <Loader2 size={14} className="animate-spin" /> : otpSent ? (resendCooldown > 0 ? `${resendCooldown}s` : 'Resend') : 'Send OTP'}
                  </button>
                )}
              </div>
            </div>

            {devOtp && !otpVerified && (
              <div className="p-3 rounded-xl bg-warning/10 border border-warning/30">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-warning">🧪 Test Mode — OTP</p>
                  <button type="button" onClick={() => { setOtp(devOtp); setTimeout(() => otpInputRef.current?.focus(), 50); }}
                    className="flex items-center gap-1 text-xs text-warning hover:text-warning/80">
                    <Copy size={11} /> Copy to field
                  </button>
                </div>
                <p className="text-lg font-mono font-bold text-warning tracking-[0.4em]">{devOtp}</p>
                <p className="text-xs text-warning/70 mt-1">Only visible in test mode (OTP_MODE=test).</p>
              </div>
            )}

            {otpSent && !otpVerified && (
              <div>
                <label htmlFor="signup-otp" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Enter 6-Digit OTP</label>
                <input
                  id="signup-otp"
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setOtpError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyOtp(); } }}
                  placeholder="••••••"
                  autoComplete="one-time-code"
                  className={`w-full bg-input border rounded-xl px-4 py-3 text-center text-2xl font-bold text-foreground tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring transition-all ${otpError ? 'border-risk' : 'border-border'}`}
                />
                {otpError && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <AlertCircle size={12} className="text-risk shrink-0" />
                    <p className="text-xs text-risk">{otpError}</p>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <button type="button" onClick={handleVerifyOtp} disabled={otpLoading || otp.length !== 6}
                    className="flex-1 h-11 gradient-primary text-white text-sm font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 mr-2">
                    {otpLoading ? <><Loader2 size={14} className="animate-spin" /> Verifying...</> : 'Verify OTP'}
                  </button>
                  <button type="button" onClick={handleResendOtp} disabled={resendCooldown > 0 || otpLoading}
                    className="flex items-center gap-1 text-xs text-secondary hover:text-secondary/80 disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors whitespace-nowrap">
                    <RefreshCw size={11} />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            )}

            {otpVerified && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-success/10 border border-success/30">
                <CheckCircle2 size={16} className="text-success shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-success">Phone Verified</p>
                  <p className="text-xs text-muted-foreground">+91 {otpVerifiedPhone} is confirmed</p>
                </div>
              </div>
            )}

            {otpVerified && (
              <button type="button" onClick={nextStep} className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2">
                Continue to Account Setup <ArrowRight size={15} />
              </button>
            )}
          </div>
        )}

        {/* ── STEP 1: ACCOUNT ── */}
        {step === 'account' && (
          <div className="space-y-4 fade-in">
            <div>
              <label htmlFor="su-fullname" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Full Name *</label>
              <input id="su-fullname" type="text" placeholder="Your full name" autoComplete="name"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                {...register('fullName', { required: 'Full name is required' })} />
              {errors.fullName && <p className="mt-1 text-xs text-risk">{errors.fullName.message}</p>}
            </div>
            <div>
              <label htmlFor="su-phone" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Phone Number *</label>
              <div className="relative">
                <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input id="su-phone" type="tel" inputMode="numeric" maxLength={10} placeholder="9876543210" autoComplete="tel" readOnly={otpVerified}
                  className="w-full bg-input border border-border rounded-xl pl-9 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all read-only:opacity-70"
                  {...register('phone', { required: 'Phone number required', pattern: { value: /^[6-9]\d{9}$/, message: 'Enter valid 10-digit number' } })} />
              </div>
              {otpVerified && <p className="mt-1 text-xs text-success flex items-center gap-1"><CheckCircle2 size={11} /> Verified</p>}
              {errors.phone && <p className="mt-1 text-xs text-risk">{errors.phone.message}</p>}
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="su-pin" className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Set 4-Digit PIN *</label>
                  <button type="button" onClick={() => setShowPin(v => !v)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {showPin ? <EyeOff size={12} /> : <Eye size={12} />} {showPin ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input id="su-pin" ref={pinRef} type={showPin ? 'text' : 'password'} inputMode="numeric" maxLength={4}
                  value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmPinRef.current?.focus(); } }}
                  placeholder="4-digit PIN" autoComplete="new-password"
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center tracking-[0.5em]" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="su-confirm-pin" className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Confirm PIN *</label>
                  <button type="button" onClick={() => setShowConfirmPin(v => !v)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {showConfirmPin ? <EyeOff size={12} /> : <Eye size={12} />} {showConfirmPin ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input id="su-confirm-pin" ref={confirmPinRef} type={showConfirmPin ? 'text' : 'password'} inputMode="numeric" maxLength={4}
                  value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); nextStep(); } }}
                  placeholder="Confirm PIN" autoComplete="new-password"
                  className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center tracking-[0.5em] ${confirmPin.length === 4 && confirmPin !== pin ? 'border-risk' : 'border-border'}`} />
                {confirmPin.length === 4 && confirmPin !== pin && <p className="mt-1 text-xs text-risk">PINs do not match</p>}
                {confirmPin.length === 4 && confirmPin === pin && <p className="mt-1 text-xs text-success flex items-center gap-1"><CheckCircle2 size={11} /> PINs match</p>}
              </div>
            </div>
            <button type="button" onClick={nextStep} className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2">
              Next <ArrowRight size={15} />
            </button>
          </div>
        )}

        {/* ── STEP 2: GYM DETAILS ── */}
        {step === 'gym' && (
          <div className="space-y-4 fade-in">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Gym Name *</label>
              <input type="text" placeholder="Iron Temple Gym"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                {...register('gymName', { required: 'Gym name is required' })} />
              {errors.gymName && <p className="mt-1 text-xs text-risk">{errors.gymName.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Gym Type</label>
              <select className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all" {...register('gymType')}>
                <option value="fitness">Fitness / General Gym</option>
                <option value="crossfit">CrossFit</option>
                <option value="yoga">Yoga Studio</option>
                <option value="martial_arts">Martial Arts</option>
                <option value="dance">Dance Studio</option>
                <option value="swimming">Swimming</option>
                <option value="sports">Sports Academy</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Address *</label>
              <input type="text" placeholder="123 MG Road"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                {...register('address', { required: 'Address is required' })} />
              {errors.address && <p className="mt-1 text-xs text-risk">{errors.address.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">City *</label>
                <input type="text" placeholder="Bengaluru"
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  {...register('city', { required: 'City is required' })} />
                {errors.city && <p className="mt-1 text-xs text-risk">{errors.city.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">State *</label>
                <input type="text" placeholder="Karnataka"
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  {...register('state', { required: 'State is required' })} />
                {errors.state && <p className="mt-1 text-xs text-risk">{errors.state.message}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">PIN/ZIP Code</label>
                <input type="text" inputMode="numeric" maxLength={6} placeholder="560001"
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  {...register('pincode')} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Gym Phone</label>
                <label className="flex items-center gap-1.5 mb-1.5 cursor-pointer">
                  <input type="checkbox" checked={sameGymPhone} onChange={e => handleSameGymPhone(e.target.checked)} className="w-3.5 h-3.5 accent-secondary rounded" />
                  <span className="text-2xs text-muted-foreground">Use owner phone</span>
                </label>
                <input type="tel" inputMode="numeric" maxLength={10} placeholder="9876543210" readOnly={sameGymPhone}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all read-only:opacity-70"
                  {...register('gymPhone')} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Gym Email</label>
              <input type="email" inputMode="email" placeholder="info@yourgym.com" autoComplete="email"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                {...register('gymEmail')} />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={prevStep} className="flex-1 h-12 bg-muted border border-border text-foreground text-sm font-semibold rounded-xl btn-press">Back</button>
              <button type="button" onClick={nextStep} className="flex-1 h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2">Next <ArrowRight size={15} /></button>
            </div>
          </div>
        )}

        {/* ── STEP 3: BRANCH DETAILS ── */}
        {step === 'branch' && (
          <div className="space-y-4 fade-in">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Branch Name *</label>
              <input type="text" placeholder="Main Branch"
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                {...register('branchName', { required: 'Branch name is required' })} />
              {errors.branchName && <p className="mt-1 text-xs text-risk">{errors.branchName.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Branch Address *</label>
              <label className="flex items-center gap-1.5 mb-1.5 cursor-pointer">
                <input type="checkbox" checked={sameBranchAddress} onChange={e => handleSameBranchAddress(e.target.checked)} className="w-3.5 h-3.5 accent-secondary rounded" />
                <span className="text-2xs text-muted-foreground">Same as gym address</span>
              </label>
              <input type="text" placeholder="Same as gym or different" readOnly={sameBranchAddress}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all read-only:opacity-70"
                {...register('branchAddress', { required: 'Branch address is required' })} />
              {errors.branchAddress && <p className="mt-1 text-xs text-risk">{errors.branchAddress.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Branch City *</label>
                <label className="flex items-center gap-1.5 mb-1.5 cursor-pointer">
                  <input type="checkbox" checked={sameBranchCity} onChange={e => handleSameBranchCity(e.target.checked)} className="w-3.5 h-3.5 accent-secondary rounded" />
                  <span className="text-2xs text-muted-foreground">Same as gym city</span>
                </label>
                <input type="text" placeholder="Bengaluru" readOnly={sameBranchCity}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all read-only:opacity-70"
                  {...register('branchCity', { required: 'City is required' })} />
                {errors.branchCity && <p className="mt-1 text-xs text-risk">{errors.branchCity.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Branch Phone</label>
                <label className="flex items-center gap-1.5 mb-1.5 cursor-pointer">
                  <input type="checkbox" checked={sameBranchPhone} onChange={e => handleSameBranchPhone(e.target.checked)} className="w-3.5 h-3.5 accent-secondary rounded" />
                  <span className="text-2xs text-muted-foreground">Use owner phone</span>
                </label>
                <input type="tel" inputMode="numeric" maxLength={10} placeholder="9876543210" readOnly={sameBranchPhone}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all read-only:opacity-70"
                  {...register('branchPhone')} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest">Location (for Geofence)</label>
                <button type="button" onClick={handleGetLocation} disabled={gettingLocation}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary/10 border border-primary/30 text-primary rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50">
                  {gettingLocation ? <Loader2 size={11} className="animate-spin" /> : <Navigation size={11} />}
                  Use My Location
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input type="text" inputMode="decimal" placeholder="Latitude (e.g. 12.9716)"
                    className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    {...register('branchLat', { validate: v => !v || (!isNaN(parseFloat(v)) && parseFloat(v) >= -90 && parseFloat(v) <= 90) || 'Invalid latitude' })} />
                </div>
                <div>
                  <input type="text" inputMode="decimal" placeholder="Longitude (e.g. 77.5946)"
                    className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    {...register('branchLng', { validate: v => !v || (!isNaN(parseFloat(v)) && parseFloat(v) >= -180 && parseFloat(v) <= 180) || 'Invalid longitude' })} />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Geofence Radius (km)</label>
              <input type="number" placeholder="5" min={0} step={0.1}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                {...register('geofenceRadius', { valueAsNumber: true })} />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={prevStep} className="flex-1 h-12 bg-muted border border-border text-foreground text-sm font-semibold rounded-xl btn-press">Back</button>
              <button type="button" onClick={nextStep} className="flex-1 h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2">Next <ArrowRight size={15} /></button>
            </div>
          </div>
        )}

        {/* ── STEP 4: DOCUMENTS ── */}
        {step === 'documents' && (
          <div className="space-y-4 fade-in">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <FileTextIcon size={18} className="text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Upload Documents</h3>
                <p className="text-xs text-muted-foreground">Optional — you can add these later from your dashboard</p>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-muted/50 border border-border/50">
              <p className="text-xs text-muted-foreground">Accepted: JPG, PNG, PDF, DOC, DOCX (max 10MB each, up to 5 files)</p>
            </div>

            <label className={`flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-all ${uploading ? 'opacity-50 cursor-not-allowed' : 'border-border hover:border-primary/50 hover:bg-primary/5'}`}>
              <Upload size={20} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Click to upload document</span>
              <span className="text-xs text-muted-foreground">or drag and drop</span>
              <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx" onChange={handleDocUpload} disabled={uploading || uploadedDocs.length >= 5} />
            </label>

            {uploadedDocs.length > 0 && (
              <div className="space-y-2">
                {uploadedDocs.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                    <FileTextIcon size={16} className="text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{doc.name}</p>
                      <p className="text-2xs text-muted-foreground">{(doc.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <select
                      value={doc.type}
                      onChange={e => updateDocType(idx, e.target.value)}
                      className="text-xs bg-input border border-border rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      {docTypes.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                    </select>
                    <button type="button" onClick={() => removeDoc(idx)} className="p-1 rounded-lg hover:bg-risk/10 text-muted-foreground hover:text-risk transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={prevStep} className="flex-1 h-12 bg-muted border border-border text-foreground text-sm font-semibold rounded-xl btn-press">Back</button>
              <button type="button" onClick={nextStep} className="flex-1 h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2">
                {uploadedDocs.length > 0 ? 'Next' : 'Skip for Now'} <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 5: PLAN SELECTION ── */}
        {step === 'plan' && (
          <div className="space-y-5 fade-in">

            {/* Free Trial Banner */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-success/15 to-primary/10 border border-success/30">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-success/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Gift size={16} className="text-success" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Start with a {trialDays}-day free trial</p>
                  <p className="text-xs text-success font-semibold mt-0.5">No payment required today — ₹0 now</p>
                  <p className="text-xs text-muted-foreground mt-1">After your free trial ends, continue with Monthly or Yearly billing. You choose when.</p>
                </div>
              </div>
            </div>

            {/* Monthly / Yearly Toggle */}
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-1 p-1 rounded-xl bg-muted border border-border">
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
                  aria-pressed={billingCycle === 'monthly'}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle('yearly')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${billingCycle === 'yearly' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
                  aria-pressed={billingCycle === 'yearly'}
                >
                  Yearly
                  {getBestYearlyDiscount() && (
                    <span className="text-2xs font-bold text-success bg-success/15 px-1.5 py-0.5 rounded-md whitespace-nowrap">
                      {getBestYearlyDiscount()}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Plan Cards */}
            {plansLoading ? (
              <div className="flex flex-col gap-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-40 rounded-2xl bg-muted/50 border border-border animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {dbPlans.map(plan => {
                  const isSelected = selectedPlan === plan.slug;
                  const price = getPlanPrice(plan);
                  const discountLabel = billingCycle === 'yearly' ? getDiscountLabel(plan) : null;
                  const memberLimit = formatLimit(plan.member_limit, 'Members');
                  const branchLimit = formatLimit(plan.max_branches, 'Branches');
                  const staffLimit = formatLimit(plan.max_staff, 'Staff');
                  const trainerLimit = formatLimit(plan.max_trainers, 'Trainers');

                  return (
                    <div
                      key={plan.id}
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onClick={() => setSelectedPlan(plan.slug)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPlan(plan.slug); } }}
                      className={`relative rounded-2xl border-2 p-4 cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-md'
                          : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                      }`}
                    >
                      {/* Popular badge */}
                      {plan.is_popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-white text-2xs font-bold shadow-sm">
                            <Star size={9} fill="currentColor" /> {plan.promo_text || 'Most Popular'}
                          </span>
                        </div>
                      )}

                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-foreground text-base">{plan.name}</h3>
                            {isSelected && (
                              <span className="flex items-center gap-1 text-2xs font-bold text-primary bg-primary/15 px-2 py-0.5 rounded-full border border-primary/30">
                                <CheckCircle2 size={10} /> Selected
                              </span>
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{plan.description}</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="flex items-baseline gap-0.5">
                            <span className="text-xs text-muted-foreground">₹</span>
                            <span className="text-2xl font-black text-foreground">{price.toLocaleString('en-IN')}</span>
                          </div>
                          <p className="text-2xs text-muted-foreground">
                            {billingCycle === 'yearly' ? '/year' : '/month'}
                          </p>
                          {discountLabel && (
                            <span className="text-2xs font-bold text-success">{discountLabel}</span>
                          )}
                        </div>
                      </div>

                      {/* Limits grid */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-muted/50">
                          <Users size={12} className="text-primary shrink-0" />
                          <div>
                            <p className="text-2xs text-muted-foreground">Members</p>
                            <p className="text-xs font-semibold text-foreground">{memberLimit.value}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-muted/50">
                          <GitBranch size={12} className="text-secondary shrink-0" />
                          <div>
                            <p className="text-2xs text-muted-foreground">Branches</p>
                            <p className="text-xs font-semibold text-foreground">{branchLimit.value}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-muted/50">
                          <UserCheck size={12} className="text-success shrink-0" />
                          <div>
                            <p className="text-2xs text-muted-foreground">Staff</p>
                            <p className="text-xs font-semibold text-foreground">{staffLimit.value}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-muted/50">
                          <Dumbbell size={12} className="text-warning shrink-0" />
                          <div>
                            <p className="text-2xs text-muted-foreground">Trainers</p>
                            <p className="text-xs font-semibold text-foreground">{trainerLimit.value}</p>
                          </div>
                        </div>
                      </div>

                      {/* Features list */}
                      {plan.features && plan.features.length > 0 && (
                        <div className="space-y-1">
                          {plan.features.slice(0, 5).map((feature, fi) => (
                            <div key={fi} className="flex items-center gap-2">
                              <Check size={11} className="text-success shrink-0" />
                              <span className="text-xs text-foreground/80">{feature}</span>
                            </div>
                          ))}
                          {plan.features.length > 5 && (
                            <p className="text-2xs text-muted-foreground pl-5">+{plan.features.length - 5} more features</p>
                          )}
                        </div>
                      )}

                      {/* Selected indicator ring */}
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Check size={11} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Trial reminder */}
            <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border/50">
              <Calendar size={14} className="text-muted-foreground shrink-0" />
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">No charge today.</span> Your {trialDays}-day free trial starts after registration. Payment only required after the trial ends.
              </p>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={prevStep} className="flex-1 h-12 bg-muted border border-border text-foreground text-sm font-semibold rounded-xl btn-press">Back</button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!selectedPlan}
                className="flex-1 h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Zap size={15} />
                Continue with {selectedPlanData?.name || 'Plan'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 6: CONFIRM & SUBMIT ── */}
        {step === 'confirm' && (
          <div className="space-y-4 fade-in">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                <CheckCircle2 size={18} className="text-success" />
              </div>
              <div>
                <h3 className="font-bold text-foreground">Review & Confirm</h3>
                <p className="text-xs text-muted-foreground">Almost there! Review your details before registering.</p>
              </div>
            </div>

            {/* Gym summary */}
            <div className="p-4 rounded-2xl bg-muted/50 border border-border space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">Your Gym</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Gym Name</span>
                <span className="text-xs font-semibold text-foreground">{watchedValues.gymName || '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">City</span>
                <span className="text-xs font-semibold text-foreground">{watchedValues.city || '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Branch</span>
                <span className="text-xs font-semibold text-foreground">{watchedValues.branchName || 'Main Branch'}</span>
              </div>
            </div>

            {/* Plan summary — the key section */}
            <div className="p-4 rounded-2xl border-2 border-primary/30 bg-primary/5 space-y-3">
              <p className="text-xs font-bold text-primary uppercase tracking-widest">Selected Plan</p>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-foreground text-base">{selectedPlanData?.name || '—'}</p>
                  {selectedPlanData?.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{selectedPlanData.description}</p>
                  )}
                </div>
                {selectedPlanData?.is_popular && (
                  <span className="flex items-center gap-1 text-2xs font-bold text-primary bg-primary/15 px-2 py-1 rounded-full">
                    <Star size={9} fill="currentColor" /> Popular
                  </span>
                )}
              </div>

              <div className="h-px bg-border/50" />

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Billing preference</span>
                  <span className="text-xs font-semibold text-foreground capitalize">{billingCycle}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Gift size={12} className="text-success" /> Today&apos;s charge
                  </span>
                  <span className="text-sm font-black text-success">₹0</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Calendar size={12} className="text-primary" /> Free trial
                  </span>
                  <span className="text-xs font-bold text-primary">{trialDays} days</span>
                </div>
                <div className="h-px bg-border/50" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">After trial ends</span>
                  <span className="text-xs font-semibold text-foreground">{getFuturePrice() || '—'}</span>
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-success/10 border border-success/20">
                <p className="text-xs text-success font-medium text-center">
                  No payment required during registration. Your {trialDays}-day free trial starts immediately.
                </p>
              </div>
            </div>

            {/* Consent */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${consent ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'}`}
                onClick={() => setConsent(!consent)}
                role="checkbox"
                aria-checked={consent}
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setConsent(!consent); }}
              >
                {consent && <Check size={12} className="text-white" strokeWidth={3} />}
              </div>
              <span className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{' '}
                <a href="#" className="text-primary hover:underline font-medium">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-primary hover:underline font-medium">Privacy Policy</a>.
                I understand that my {trialDays}-day free trial starts today and no payment is required now.
              </span>
            </label>

            <div className="flex gap-3">
              <button type="button" onClick={prevStep} className="flex-1 h-12 bg-muted border border-border text-foreground text-sm font-semibold rounded-xl btn-press">Back</button>
              <button
                type="submit"
                disabled={isLoading || !consent}
                className="flex-1 h-12 gradient-primary text-white text-sm font-bold rounded-xl btn-press flex items-center justify-center gap-2 disabled:opacity-50"
                aria-label={`Start ${trialDays}-day free trial with ${selectedPlanData?.name || 'selected'} plan`}
              >
                {isLoading ? (
                  <><Loader2 size={15} className="animate-spin" /> Registering...</>
                ) : (
                  <><Gift size={15} /> Start {trialDays}-Day Free Trial</>
                )}
              </button>
            </div>

            <p className="text-center text-2xs text-muted-foreground">
              🔒 Secure registration · No credit card required · Cancel anytime
            </p>
          </div>
        )}

      </form>
    </div>
  );
}